<template>
    <div class="dashboard-view flex flex-column gap-6 p-3 justify-content-center align-items-center">
        <div class="flex flex-column justify-content-center gap-6 align-items-center w-full">
            <div class="dashboard-view-container dashboard-view-container__first flex gap-6 w-full justify-content-center">
                <div class="w-6 flex justify-content-center flex-column align-items-center dashboard-view-box">
                    <p class="text-center">Total number of registered horses by gender</p>
                    <Chart type="doughnut" :data="chartsData.nRegHorByGen" class="w-full md:w-30rem" />
                </div>
                <div class="w-6 flex justify-content-center flex-column align-items-center dashboard-view-box">
                    <p class="text-center">Total number of registered horses by city</p>
                    <Chart type="pie" :data="chartsData.nRegHorByCit" class="w-full md:w-30rem" />
                </div>
            </div>
            <div class="dashboard-view-container dashboard-view-container__second flex gap-6 w-full">
                <div class="w-6 flex justify-content-center flex-column align-items-center dashboard-view-box relative">
                    <CustomButton label="Reset zoom" @click="resetZoom('impChart')" class="reset-zoom-button" />
                    <p class="text-center">Imported horses by country and year</p>
                    <div class="responsive-chart h-auto w-full">
                        <ul class="legend-container" v-if="chartsData?.impHorByCoY?.datasets">
                            <li v-for="data in chartsData.impHorByCoY.datasets" class="legend-container__item">
                                <div v-if="data.label" :style="computedStyle(data)"></div>
                                <span>{{ data.label }}</span>
                            </li>
                        </ul>
                        <Chart type="bar" :data="chartsData.impHorByCoY" class="h-auto w-full flex justify-content-center" :options="chartOptions" ref="impChart" />
                    </div>
                </div>
                <div class="w-6 flex justify-content-center flex-column align-items-center dashboard-view-box relative">
                    <p class="text-center">Exported horses by country and year</p>
                    <CustomButton label="Reset zoom" @click="resetZoom('expChart')" class="reset-zoom-button" />
                    <div class="responsive-chart h-auto w-full">
                        <ul class="legend-container" v-if="chartsData?.exHorByCoY?.datasets">
                            <li v-for="data in chartsData.exHorByCoY.datasets" class="legend-container__item">
                                <div v-if="data.label" :style="computedStyle(data)"></div>
                                <span>{{ data.label }}</span>
                            </li>
                        </ul>
                        <Chart type="bar" :data="chartsData.exHorByCoY" class="h-auto w-full flex justify-content-center" :options="chartOptions" ref="expChart" />
                    </div>
                </div>
            </div>
        </div>
        <div class="dashboard-view-container dashboard-view-container__third flex align-items-stretch justify-content-center w-full gap-6">
            <div class="w-3 flex justify-content-center flex-column align-items-center dashboard-view-box">
                <p class="text-center">Total DNA analysis in progress</p>
                {{ chartsData.totDNA }}
            </div>
            <div class="w-3 flex justify-content-center flex-column align-items-center dashboard-view-box">
                <p class="text-center">Total passports ready for printing</p>
                {{ chartsData.totPass }}
            </div>
            <div class="w-3 flex justify-content-center flex-column align-items-center dashboard-view-box">
                <p class="text-center">Total registration waiting approvals</p>
                {{ chartsData.totReg }}
            </div>
            <div class="w-3 flex justify-content-center flex-column align-items-center dashboard-view-box">
                <p class="text-center">Average registered by days</p>
                {{ chartsData.avgReg }}
            </div>
        </div>
        <div class="dashboard-view-container dashboard-view-container__fourth flex align-items-stretch justify-content-center w-full gap-6">
            <div class="w-6 flex justify-content-center flex-column align-items-center dashboard-view-box relative">
                <p class="text-center">Number of new foal by city and year comparing the last 2 years</p>
                <CustomButton label="Reset zoom" @click="resetZoom('nFChart')" class="reset-zoom-button" />
                <div class="responsive-chart h-auto w-full">
                    <ul class="legend-container" v-if="chartsData?.nFolCoY?.datasets">
                        <li v-for="data in chartsData.nFolCoY.datasets" class="legend-container__item">
                            <div v-if="data.label" :style="computedStyle(data)"></div>
                            <span>{{ data.label }}</span>
                        </li>
                    </ul>
                    <Chart type="bar" :data="chartsData.nFolCoY" class="h-auto w-full" :options="stackedChartOptions" ref="nFChart" />
                </div>
            </div>
            <div class="w-6 flex justify-content-center flex-column align-items-center dashboard-view-box relative">
                <p class="text-center">Number of registered foals over 5 years</p>
                <CustomButton label="Reset zoom" @click="resetZoom('rFChart')" class="reset-zoom-button" />
                <div class="responsive-chart h-auto w-full">
                    <ul class="legend-container" v-if="chartsData?.nRegFol?.datasets">
                        <li v-for="data in chartsData.nRegFol.datasets" class="legend-container__item">
                            <div v-if="data.label" :style="computedStyle(data)"></div>
                            <span>{{ data.label }}</span>
                        </li>
                    </ul>
                    <Chart type="line" :data="chartsData.nRegFol" class="h-auto w-full" :options="chartOptions" ref="rFChart" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Chart from "primevue/chart";
    import {
        getNumberOfRegisteredHorsesByGender,
        getNumberOfRegisteredHorsesByCity,
        getImportedHorsesByCountryAndYear,
        getExportedHorsesByCountryAndYear,
        getTotalDNAAnalysisInProgress,
        getTotalPassportsReadyForPrinting,
        getTotalRegistrationWaitingApprovals,
        getAverageRegisteredByDays,
        getNumberOfFoalByCityAndYear,
        getNumberOfRegisteredFoals,
    } from "@/swagger/vue-api-client";
    import CustomButton from "@/components/form/CustomButton.vue";

    export default {
        name: "DashboardView",

        components: {
            Chart,
            CustomButton,
        },

        data() {
            return {
                horseBreed: localStorage.getItem("horseBreed"),
                chartsData: {
                    nRegHorByGen: null,
                    nRegHorByCit: null,
                    impHorByCoY: null,
                    exHorByCoY: null,
                    totDNA: null,
                    totPass: null,
                    totReg: null,
                    avgReg: null,
                    nFolCoY: null,
                    nRegFol: null,
                },
                chartOptions: {
                    plugins: {
                        zoom: {
                            zoom: {
                                wheel: {
                                    enabled: true,
                                },
                                pinch: {
                                    enabled: true,
                                },
                                mode: "xy",
                                limits: {
                                    x: {
                                        minRange: 4,
                                    },
                                },
                            },
                            limits: {
                                y: { min: 0 },
                            },
                            pan: {
                                enabled: true,
                                mode: "xy",
                                limits: {
                                    x: {
                                        min: 0,
                                        max: 100,
                                    },
                                    y: { min: 0 },
                                },
                            },
                        },
                        legend: {
                            display: false,
                        },
                    },
                    scales: {
                        x: {
                            ticks: {
                                autoSkip: false,
                            },
                        },
                        y: {
                            min: 0,
                            beginAtZero: true,
                            suggestedMax: 10,
                            ticks: {
                                stepSize: 1,
                                callback(value) {
                                    return Number.isInteger(value) ? value : "";
                                },
                            },
                        },
                    },
                },
                stackedChartOptions: {
                    plugins: {
                        zoom: {
                            zoom: {
                                wheel: {
                                    enabled: true,
                                },
                                pinch: {
                                    enabled: true,
                                },
                                mode: "xy",
                                limits: {
                                    x: {
                                        minRange: 4,
                                    },
                                },
                            },
                            limits: {
                                y: { min: 0 },
                            },
                            pan: {
                                enabled: true,
                                mode: "xy",
                                limits: {
                                    x: {
                                        min: 0,
                                        max: 100,
                                    },
                                    y: { min: 0 },
                                },
                            },
                        },
                        legend: {
                            display: false,
                        },
                    },
                    scales: {
                        x: {
                            stacked: true,
                            ticks: {
                                autoSkip: false,
                            },
                        },
                        y: {
                            stacked: true,
                            min: 0,
                            beginAtZero: true,
                            suggestedMax: 10,
                            ticks: {
                                stepSize: 1,
                                callback(value) {
                                    return Number.isInteger(value) ? value : "";
                                },
                            },
                        },
                    },
                },
            };
        },

        beforeMount() {
            getNumberOfRegisteredHorsesByGender({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.nRegHorByGen = response.data;
                });
            getNumberOfRegisteredHorsesByCity({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.nRegHorByCit = response.data;
                });
            getImportedHorsesByCountryAndYear({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.impHorByCoY = response.data;
                });
            getExportedHorsesByCountryAndYear({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.exHorByCoY = response.data;

                    // this.chartsData.exHorByCoY = {
                    //     labels: [
                    //         "Australia",
                    //         "Canada",
                    //         "Saudi Arabia",
                    //         "United Kingdom",
                    //     ],
                    //     datasets: [
                    //         {
                    //             label: "2025",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2024",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2023",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2022",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2021",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2020",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2019",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2018",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2017",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2016",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2015",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1
                    //             ]
                    //         },
                    //         {
                    //             label: "2014",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2013",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2012",
                    //             data: [
                    //                 3000,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2011",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2010",
                    //             data: [
                    //                 100,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2009",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2008",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2007",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //         {
                    //             label: "2006",
                    //             data: [
                    //                 3,
                    //                 2,
                    //                 1,
                    //                 1,
                    //             ],
                    //         },
                    //     ]
                    // }
                });
            getTotalDNAAnalysisInProgress({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.totDNA = response.data;
                });
            getTotalPassportsReadyForPrinting({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.totPass = response.data;
                });
            getTotalRegistrationWaitingApprovals({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.totReg = response.data;
                });
            getAverageRegisteredByDays({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.avgReg = response.data;
                });
            getNumberOfFoalByCityAndYear({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.nFolCoY = response.data;
                });
            getNumberOfRegisteredFoals({horseBreed: this.horseBreed})
                .then((response) => {
                    this.chartsData.nRegFol = response.data;

                    // this.chartsData.nRegFol = {
                    //     labels: [
                    //         "2025",
                    //         "2024",
                    //         "2023",
                    //         "2022",
                    //         "2021",
                    //     ],
                    //     datasets: [
                    //         {
                    //             label: 'First Dataset',
                    //             data: [
                    //                 100,
                    //                 2,
                    //                 1,
                    //                 15,
                    //                 20,
                    //             ],
                    //             // fill: false,
                    //             // borderColor: "red",
                    //             // tension: 0.4,
                    //         },
                    //     ]
                    // }
                });
        },

        methods: {
            resetZoom(ref) {
                const chartInstance = this.$refs[ref].chart;
                chartInstance.resetZoom();
            },
            computedStyle(data) {
                return `width: 12px; height: 12px; border-color: ${data.borderColor}; background-color: ${data.backgroundColor}`;
            },
        },
    };
</script>

<style lang="scss">
    @import "../../assets/theme/mytheme/variables";

    .dashboard-view {
        .dashboard-view-container {
            &__first, &__second, &__fourth {
                @media screen and (max-width: 1050px) {
                    flex-direction: column;
                    width: 90%;
                    .dashboard-view-box {
                        width: 100% !important;
                    }
                }
                @media screen and (max-width: 650px) {
                    .responsive-chart {
                        display: flex;
                        justify-content: center !important;
                        overflow-x: auto;
                        padding-bottom: 2rem;
                        .p-chart {
                            min-width: 524px;
                        }
                    }
                }
            }
            &__third {
                @media screen and (max-width: 1050px) {
                    flex-wrap: wrap;
                    .dashboard-view-box {
                        width: calc(50% - 1.5rem) !important;
                    }
                }
                @media screen and (max-width: 650px) {
                    .dashboard-view-box {
                        width: 100% !important;
                    }
                }
            }
            .dashboard-view-box {
                background: var(--primary-color-moretransparent);
                padding: 3rem;
                border-radius: 7px;
                .reset-zoom-button {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    padding: 5px 10px;
                    font-size: 10px;
                }
                .legend-container {
                    list-style: none;
                    display: flex;
                    gap: 1rem;
                    flex-wrap: wrap;
                    max-height: 100px;
                    overflow-y: auto;
                    padding: 1rem;
                    &::-webkit-scrollbar {
                        width: 8px;
                    }
                    &::-webkit-scrollbar-track {
                        background: var(--primary-color);
                        box-shadow: inset 0 0 2px grey;
                    }
                    &::-webkit-scrollbar-thumb {
                        background: var(--secondary-color);
                    }
                    &__item {
                        display: flex;
                        justify-content: start;
                        align-items: center;
                        gap: 0.25rem;
                        font-size: 12px;
                        width: fit-content;
                        span {
                            line-height: 1;
                        }
                    }
                }
            }
        }
        @media screen and (max-width: 650px) {
            padding: 0 !important;
        }
    }
</style>
